/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, OnInit } from '@angular/core';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { Absence, AbsenceClass, AbsenceSummaryCount } from '@shared/models';
import { departmentTint } from '@shared/utils';
import { AbsenceDepartmentGroup } from '@tours/store/absences-by-department';
import * as TourActions from '@tours/store/tours.actions';
import { getCurrentAbsencesByDepartment } from '@tours/store/tours.selectors';
import { Observable } from 'rxjs';

const FALLBACK_ABSENCE_CLASS_ICON = 'event_busy';
const FALLBACK_ABSENCE_CLASS_COLOR = '#757575';

@Component({
    selector: 'app-absences-info',
    templateUrl: './absences-info.component.html',
    styleUrls: ['./absences-info.component.scss'],
    standalone: false
})
export class AbsencesInfoComponent implements OnInit {

  absenceGroups$: Observable<AbsenceDepartmentGroup[]>;

  constructor(private store: Store<State>) { }

  ngOnInit(): void {
    this.store.dispatch(new TourActions.LoadCurrentAbsences());
    this.store.dispatch(new TourActions.LoadAbsenceSummary());
    this.absenceGroups$ = this.store.select(getCurrentAbsencesByDepartment);
  }

  /**
   * Hinterlegung eines Blocks. Ziele ohne Abteilung bekommen ein neutrales Grau in
   * derselben Deckkraft wie der Standard einer neuen Abteilung.
   */
  tintOf(group: AbsenceDepartmentGroup): string {
    return departmentTint(group.department?.color, group.department?.opacity ?? 8);
  }

  /** Vollfarbe der Kante; ohne Abteilung ebenfalls neutrales Grau. */
  edgeColorOf(group: AbsenceDepartmentGroup): string {
    return group.department ? group.department.color : '#BDBDBD';
  }

  presentShare(count: AbsenceSummaryCount): number {
    if (count.total <= 0) {
      return 0;
    }

    const present = Math.min(Math.max(count.present, 0), count.total);

    return Math.round(present / count.total * 100);
  }

  absentShare(count: AbsenceSummaryCount): number {
    return 100 - this.presentShare(count);
  }

  driverCountOf(group: AbsenceDepartmentGroup): AbsenceSummaryCount | undefined {
    return withAbsences(group.summary?.drivers);
  }

  vehicleCountOf(group: AbsenceDepartmentGroup): AbsenceSummaryCount | undefined {
    return withAbsences(group.summary?.vehicles);
  }

  labelOf(absence: Absence): string {
    return absence.driver
      ? `${absence.driver.firstName} ${absence.driver.lastName}`
      : absence.vehicle.licensePlate;
  }

  iconOf(absenceClass: AbsenceClass): string {
    return absenceClass.icon || FALLBACK_ABSENCE_CLASS_ICON;
  }

  colorOf(absenceClass: AbsenceClass): string {
    return absenceClass.color || FALLBACK_ABSENCE_CLASS_COLOR;
  }
}

function withAbsences(count?: AbsenceSummaryCount): AbsenceSummaryCount | undefined {
  return count && count.absent > 0 ? count : undefined;
}
